/**
 * ToolsSubmenu — drill-in сабменю панели инструментов (Artlist-style).
 *
 * Открывается из строки-пикера («Стиль ›», «Голос ›») ВТОРОЙ стеклянной
 * панелью, примыкающей к основному поповеру инструментов сбоку (как
 * Genre ›/Mood › у toolkit.artlist.io). Контент произвольный (children) —
 * функционал пикеров остаётся их собственным, сабменю лишь «транспорт».
 *
 * Поведение:
 *   • позиционируется у ПРАВОГО края основного поповера (нет места → слева),
 *     нижняя кромка выровнена с поповером (как у Artlist);
 *   • Escape закрывает ТОЛЬКО сабменю (capture + stopImmediatePropagation —
 *     основной поповер не должен закрыться тем же нажатием);
 *   • клик вне сабменю закрывает его; основной поповер сам решает свою
 *     судьбу (клик по фону страницы закроет и его — это ок, как у Artlist).
 *     Клики ВНУТРИ сабменю основной поповер игнорирует по классу
 *     `.gm-tools-submenu` (см. BottomBarPopover).
 *   • анимация входа — CSS-keyframes на маунте (без state-хореографии).
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface SubmenuPos {
	left: number
	bottom: number
	maxHeight: number
}

interface Props {
	open: boolean
	/** Ref строки-якоря (读аем .current только в эффектах/колбэках). */
	anchorRef: React.RefObject<HTMLElement | null>
	onClose: () => void
	width?: number
	className?: string
	children: React.ReactNode
}

function calcPos(anchorEl: HTMLElement, width: number): SubmenuPos {
	// Основная привязка — панель поповера, в которой живёт строка-якорь.
	const pop = anchorEl.closest('.bbar-popover') as HTMLElement | null
	const rect = (pop ?? anchorEl).getBoundingClientRect()
	const gap = 8
	const vw = window.innerWidth
	const vh = window.innerHeight

	// Справа от поповера; не влезает — слева; совсем негде — поверх с отступом.
	let left = rect.right + gap
	if (left + width > vw - 8) left = rect.left - width - gap
	if (left < 8) left = Math.max(8, Math.min(rect.left, vw - width - 8))

	// Нижние кромки выровнены (Artlist). Вверх — до края вьюпорта.
	const bottom = Math.max(8, vh - rect.bottom)
	const maxHeight = vh - bottom - 16
	return { left, bottom, maxHeight }
}

export default function ToolsSubmenu({ open, anchorRef, onClose, width = 340, className = '', children }: Props) {
	const ref = useRef<HTMLDivElement>(null)
	const [pos, setPos] = useState<SubmenuPos | null>(null)

	// Замер позиции — в rAF-колбэке (после открытия) + на resize/scroll.
	// pos сознательно НЕ сбрасывается при закрытии: при повторном открытии
	// один кадр показывается прежняя позиция, rAF тут же поправит.
	useEffect(() => {
		if (!open) return
		const measure = () => {
			const el = anchorRef.current
			if (el) setPos(calcPos(el, width))
		}
		const raf = requestAnimationFrame(measure)
		window.addEventListener('resize', measure)
		window.addEventListener('scroll', measure, true)
		return () => {
			cancelAnimationFrame(raf)
			window.removeEventListener('resize', measure)
			window.removeEventListener('scroll', measure, true)
		}
	}, [open, anchorRef, width])

	// Escape закрывает ТОЛЬКО сабменю: capture-фаза + stopImmediatePropagation,
	// чтобы bubble-слушатель основного поповера не сработал тем же нажатием.
	useEffect(() => {
		if (!open) return
		function onKey(e: KeyboardEvent) {
			if (e.key !== 'Escape') return
			e.stopImmediatePropagation()
			onClose()
		}
		document.addEventListener('keydown', onKey, true)
		return () => document.removeEventListener('keydown', onKey, true)
	}, [open, onClose])

	// Клик вне сабменю (и вне якорной строки) — закрыть сабменю. Клики в
	// оверлеях поверх (шторки, тур) игнорируем.
	useEffect(() => {
		if (!open) return
		function onDown(e: MouseEvent) {
			const t = e.target as HTMLElement
			if (ref.current?.contains(t)) return
			if (anchorRef.current?.contains(t)) return
			if (t.closest?.('.sheet-backdrop, .spotlight-tour')) return
			onClose()
		}
		document.addEventListener('mousedown', onDown, true)
		return () => document.removeEventListener('mousedown', onDown, true)
	}, [open, anchorRef, onClose])

	if (!open || !pos) return null

	return createPortal(
		<div
			ref={ref}
			className={`gm-tools-submenu ${className}`}
			role="dialog"
			style={{
				position: 'fixed',
				left: pos.left,
				bottom: pos.bottom,
				width,
				maxHeight: Math.min(pos.maxHeight, 560),
				zIndex: 10000,
			}}
		>
			{children}
		</div>,
		document.body,
	)
}
